// Piezas pequenas reutilizables: avatar, pildoras, graficas, vacios, errores, telefono clicable.
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { phone } from '../phone/store';
import { hue, initials } from '../lib/format';
import { Icon, type IconName } from '../lib/icons';
import { formatPhone } from '../lib/phone-number';

export function Avatar({ nombre, size, square }: { nombre: string; size?: 'sm' | 'lg'; square?: boolean }) {
  return (
    <span className={`av ${size ?? ''} ${square ? 'sq' : ''}`} style={{ ['--av-h' as string]: hue(nombre) }} aria-hidden>
      {initials(nombre)}
    </span>
  );
}

export function Pill({ tone, dot, children, title }: { tone?: 'acc' | 'ok' | 'warn' | 'bad'; dot?: boolean; children: ReactNode; title?: string }) {
  return (
    <span className={`pill ${tone ?? ''}`} title={title}>
      {dot ? <span className="pd" /> : null}
      {children}
    </span>
  );
}

export function IconDot({ name, tone }: { name: IconName; tone?: 'acc' | 'ok' | 'warn' | 'bad' }) {
  return (
    <span className={`ico-dot ${tone ?? ''}`}>
      <Icon name={name} />
    </span>
  );
}

// Numero que se marca con un clic desde cualquier pantalla.
export function Tel({ numero, contacto, label }: { numero: string; contacto?: { id: string; nombre: string; empresa?: string } | null; label?: string }) {
  if (!numero) return <span className="sb">—</span>;
  return (
    <button
      type="button"
      className="tel"
      data-tel={numero}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        void phone.dial(numero, contacto ?? null);
      }}
      aria-label={`Llamar a ${contacto?.nombre ?? formatPhone(numero)}`}
      title="Llamar"
    >
      {label ?? formatPhone(numero)}
    </button>
  );
}

export function Spark({ values }: { values: number[] }) {
  const w = 72;
  const h = 22;
  if (values.length < 2) return <svg className="spark" viewBox={`0 0 ${w} ${h}`} aria-hidden />;
  const mx = Math.max(...values);
  const mn = Math.min(...values);
  const sx = w / (values.length - 1);
  const sy = (v: number) => h - 2 - ((v - mn) / (mx - mn || 1)) * (h - 5);
  const pts = values.map((v, i) => [i * sx, sy(v)]);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join('');
  const last = pts[pts.length - 1];
  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} aria-hidden>
      <path className="a" d={`${d}L${w} ${h}L0 ${h}Z`} />
      <path className="l" d={d} />
      <circle cx={last[0]} cy={last[1]} r={2.2} />
    </svg>
  );
}

function niceStep(max: number, t = 4, enteros = false) {
  if (max <= 0) return 1;
  const raw = max / t;
  if (enteros && raw < 1) return 1;
  const p = 10 ** Math.floor(Math.log10(raw));
  const n = raw / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * p;
}

// Ancho real del contenedor, para dibujar en pixeles (el texto no se agranda con la caja).
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(600);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(240, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

// Barras verticales dibujadas a escala: la misma escala ubica barras, rejilla y etiquetas.
export function BarChart({ values, labels, height = 190, fmt = (v: number) => String(v), highlight = -1, every: everyProp, label, enteros = false }: { values: number[]; labels: string[]; height?: number; fmt?: (v: number) => string; highlight?: number; every?: number; label: string; enteros?: boolean }) {
  const [box, W] = useWidth<HTMLDivElement>();
  const H = height;
  // Si no caben todas las etiquetas del eje X, se muestra una de cada N.
  const every = everyProp ?? Math.max(1, Math.ceil((values.length * 26) / Math.max(1, W - 60)));
  const pl = 56;
  const pr = 6;
  const pt = 10;
  const pb = 24;
  const mx = Math.max(enteros ? 1 : 0.0001, ...values);
  const step = niceStep(mx, 4, enteros);
  const top = Math.ceil(mx / step) * step;
  const iw = W - pl - pr;
  const ih = H - pt - pb;
  const bw = iw / Math.max(1, values.length);
  const ticks: number[] = [];
  for (let t = 0; t <= top + 1e-9; t += step) ticks.push(t);
  return (
    <div ref={box} style={{ width: '100%' }}>
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label={label}>
      {ticks.map((t) => {
        const y = pt + ih - (t / top) * ih;
        return (
          <g key={t}>
            <line className="grid" x1={pl} x2={W - pr} y1={y} y2={y} />
            <text className="ax" x={pl - 8} y={y + 4} textAnchor="end">
              {fmt(t)}
            </text>
          </g>
        );
      })}
      {values.map((v, i) => {
        const bh = (v / top) * ih;
        const x = pl + i * bw + bw * 0.17;
        return (
          <g key={i}>
            <rect className={`bar${i === highlight ? ' hl' : ''}`} x={x.toFixed(1)} y={(pt + ih - bh).toFixed(1)} width={(bw * 0.66).toFixed(1)} height={Math.max(0, bh).toFixed(1)} rx={3}>
              <title>{`${labels[i]}: ${fmt(v)}`}</title>
            </rect>
            {i % every === 0 ? (
              <text className="ax" x={(x + bw * 0.33).toFixed(1)} y={H - 6} textAnchor="middle">
                {labels[i]}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
    </div>
  );
}

export function HBars({ items, fmt }: { items: { nombre: string; valor: number; detalle?: string }[]; fmt: (v: number) => string }) {
  const mx = Math.max(1, ...items.map((i) => i.valor));
  if (!items.length) return <Empty titulo="Sin datos todavía" texto="Aparecerán cuando haya llamadas salientes con costo." />;
  return (
    <div className="hbars">
      {items.map((it) => (
        <div className="hbar" key={it.nombre}>
          <span className="nn" title={it.nombre}>
            {it.nombre}
            {it.detalle ? <small>{it.detalle}</small> : null}
          </span>
          <span className="tr">
            <span className="fl" style={{ width: `${((it.valor / mx) * 100).toFixed(1)}%` }} />
          </span>
          <span className="vv">{fmt(it.valor)}</span>
        </div>
      ))}
    </div>
  );
}

export function Empty({ titulo, texto, children, icon }: { titulo: string; texto?: string; children?: ReactNode; icon?: IconName }) {
  return (
    <div className="empty">
      {icon ? <IconDot name={icon} /> : null}
      <b>{titulo}</b>
      {texto ? <span>{texto}</span> : null}
      {children}
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="err-box" role="alert">
      <Icon name="alert" />
      <span style={{ flex: 1 }}>{(error as Error)?.message || 'Algo falló al cargar.'}</span>
      {onRetry ? (
        <button className="btn sm" onClick={onRetry}>
          Reintentar
        </button>
      ) : null}
    </div>
  );
}

export function Skeleton({ rows = 4, height = 18 }: { rows?: number; height?: number }) {
  return (
    <div className="rows" aria-busy="true" aria-label="Cargando">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skel" style={{ height, margin: '8px 6px', width: `${70 + ((i * 13) % 30)}%` }} />
      ))}
    </div>
  );
}

// Funcion pendiente de integracion: se muestra el diseno y se explica que falta, sin fingir datos.
export function Soon({ titulo, children, icon = 'clock' }: { titulo: string; children: ReactNode; icon?: IconName }) {
  return (
    <div className="soon">
      <IconDot name={icon} />
      <div>
        <b>{titulo}</b>
        {children}
      </div>
    </div>
  );
}

export function Kpi({ label, value, detail, spark, tone }: { label: string; value: ReactNode; detail?: ReactNode; spark?: number[]; tone?: 'warn' }) {
  return (
    <div className={`kpi ${tone ?? ''}`}>
      <span className="lbl">{label}</span>
      <span className="v">{value}</span>
      <span className="d">
        <span>{detail}</span>
        {spark ? <Spark values={spark} /> : null}
      </span>
    </div>
  );
}
